import React, { useState, useEffect } from 'react';
import { Shop, ShopProduct } from '../services/shopService';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './OrderPlacement.css';

interface CartItem {
  product: ShopProduct;
  shop: Shop;
  quantity: number;
}

interface ShopOrder {
  shop: Shop;
  items: CartItem[];
  total: number;
  shippingAddress: string;
  paymentMethod: string;
  notes?: string;
}

interface OrderPlacementProps {
  shopOrders: { shop: Shop; items: CartItem[] }[];
  onOrderPlaced: (orderIds: string[]) => void;
  onCancel: () => void;
}

const OrderPlacement: React.FC<OrderPlacementProps> = ({
  shopOrders,
  onOrderPlaced,
  onCancel
}) => {
  console.log('OrderPlacement component rendered with shopOrders:', shopOrders);
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [shippingAddress, setShippingAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderIds, setOrderIds] = useState<string[]>([]);

  // Initialize orders from shop orders
  useEffect(() => {
    const initializedOrders: ShopOrder[] = shopOrders.map(shopOrder => ({
      shop: shopOrder.shop,
      items: shopOrder.items,
      total: shopOrder.items.reduce((total, item) => total + (item.product.price * item.quantity), 0),
      shippingAddress: '',
      paymentMethod: 'credit_card',
      notes: ''
    }));
    setOrders(initializedOrders);
  }, [shopOrders]);

  const steps = [
    { id: 'address', title: 'Shipping Address', icon: '📍' },
    { id: 'payment', title: 'Payment Method', icon: '💳' },
    { id: 'review', title: 'Review Orders', icon: '📋' },
    { id: 'confirmation', title: 'Confirmation', icon: '✅' }
  ];

  const getShopTypeIcon = (type: string) => {
    switch (type) {
      case 'medical': return '🏥';
      case 'bakery': return '🥖';
      case 'restaurant': return '🍛';
      case 'grocery': return '🛒';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      default: return '🏪';
    }
  };

  const calculateGrandTotal = () => {
    return orders.reduce((total, order) => total + order.total, 0);
  };

  const handleAddressSubmit = () => {
    // Update all orders with the shipping address
    setOrders(prevOrders => 
      prevOrders.map(order => ({ ...order, shippingAddress }))
    );
    setCurrentStep(1);
  };

  const handlePaymentSubmit = () => {
    // Update all orders with the payment method
    setOrders(prevOrders => 
      prevOrders.map(order => ({ ...order, paymentMethod }))
    );
    setCurrentStep(2);
  };

  const handlePlaceOrders = async () => {
    setIsProcessing(true);
    try {
      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        alert('Please log in to place orders');
        setIsProcessing(false);
        return;
      }

      const newOrderIds: string[] = [];
      
      // Place orders for each shop
      for (const order of orders) {
        try {
          const orderData = {
            user_id: user.id, // Use authenticated user's ID
            shop_id: order.shop.id,
            items: order.items.map(item => ({
              product_id: item.product.id,
              product_name: item.product.name,
              product_image: item.product.image || null,
              product_brand: item.product.brand || null,
              quantity: item.quantity,
              unit_price: item.product.price
            })),
            shipping_address_id: "test-address-1", // TODO: Use actual address ID
            billing_address_id: "test-address-1", // TODO: Use actual address ID
            payment_method: order.paymentMethod,
            notes: notes || `Order from ${order.shop.name}`
          };

          const response = await apiService.createOrder(orderData);
          console.log('Order creation response:', response);
          
          // Extract order ID from response - try multiple possible field names
          const orderId = response.order_id || response.id || response.orderId || response.order_number;
          if (orderId) {
            newOrderIds.push(orderId);
            console.log('Order created with ID:', orderId);
          } else {
            console.error('No order ID found in response:', response);
            // Generate a temporary ID if none found
            newOrderIds.push(`temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
          }
        } catch (error) {
          console.error(`Failed to place order for ${order.shop.name}:`, error);
          // Continue with other orders even if one fails
        }
      }

      setOrderIds(newOrderIds);
      setCurrentStep(3);
    } catch (error) {
      console.error('Error placing orders:', error);
      alert('Failed to place some orders. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <h3>📍 Shipping Address</h3>
            <p>Enter your shipping address for all orders</p>
            <div className="form-group">
              <label htmlFor="shipping-address">Full Address:</label>
              <textarea
                id="shipping-address"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder="Enter your complete shipping address..."
                rows={4}
                required
              />
            </div>
            <div className="step-actions">
              <button onClick={onCancel} className="cancel-btn">
                Cancel
              </button>
              <button 
                onClick={handleAddressSubmit}
                disabled={!shippingAddress.trim()}
                className="next-btn"
              >
                Next: Payment Method
              </button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="step-content">
            <h3>💳 Payment Method</h3>
            <p>Select your preferred payment method</p>
            <div className="form-group">
              <label>Payment Method:</label>
              <div className="payment-options">
                <label className="payment-option">
                  <input
                    type="radio"
                    value="credit_card"
                    checked={paymentMethod === 'credit_card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>💳 Credit Card</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    value="debit_card"
                    checked={paymentMethod === 'debit_card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>💳 Debit Card</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>🅿️ PayPal</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    value="cash_on_delivery"
                    checked={paymentMethod === 'cash_on_delivery'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span>💰 Cash on Delivery</span>
                </label>
              </div>
            </div>
            <div className="step-actions">
              <button onClick={() => setCurrentStep(0)} className="back-btn">
                Back
              </button>
              <button onClick={handlePaymentSubmit} className="next-btn">
                Next: Review Orders
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3>📋 Review Your Orders</h3>
            <p>Please review your orders before placing them</p>
            
            <div className="orders-summary">
              {orders.map((order, _index) => (
                <div key={order.shop.id} className="order-summary">
                  <div className="order-header">
                    <h4>
                      {getShopTypeIcon(order.shop.type)} {order.shop.name}
                    </h4>
                    <span className="order-total">${order.total.toFixed(2)}</span>
                  </div>
                  
                  <div className="order-items">
                    {order.items.map(item => (
                      <div key={`${item.product.id}-${item.shop.id}`} className="order-item">
                        <span className="item-name">{item.product.name}</span>
                        <span className="item-quantity">x{item.quantity}</span>
                        <span className="item-price">${(item.product.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="order-details">
                    <p><strong>Shipping Address:</strong> {order.shippingAddress}</p>
                    <p><strong>Payment Method:</strong> {order.paymentMethod.replace('_', ' ').toUpperCase()}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grand-total">
              <h4>Grand Total: ${calculateGrandTotal().toFixed(2)}</h4>
              <p>Across {orders.length} shop{orders.length > 1 ? 's' : ''}</p>
            </div>

            <div className="step-actions">
              <button onClick={() => setCurrentStep(1)} className="back-btn">
                Back
              </button>
              <button 
                onClick={handlePlaceOrders}
                disabled={isProcessing}
                className="place-order-btn"
              >
                {isProcessing ? 'Placing Orders...' : 'Place Orders'}
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="success-message">
              <div className="success-icon">✅</div>
              <h3>Orders Placed Successfully!</h3>
              <p>Your orders have been placed and are being processed.</p>
              
              <div className="order-ids">
                <h4>Order IDs:</h4>
                {orderIds.length > 0 ? (
                  <ul>
                    {orderIds.map((orderId, index) => (
                      <li key={orderId}>
                        Order #{orderId} - {orders[index]?.shop.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-order-ids">
                    <p>Orders have been placed successfully, but order IDs are being processed.</p>
                    <p>You will receive confirmation emails with your order details shortly.</p>
                  </div>
                )}
              </div>

              <div className="next-steps">
                <h4>What's Next?</h4>
                <ul>
                  <li>You'll receive confirmation emails for each order</li>
                  <li>Track your orders in the "My Orders" section</li>
                  <li>Each shop will handle their own delivery</li>
                </ul>
              </div>
            </div>

            <div className="step-actions">
              <button onClick={() => onOrderPlaced(orderIds)} className="done-btn">
                Done
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="order-placement">
      <div className="order-header">
        <h2>🛒 Place Your Orders</h2>
        <p>Complete your purchase across multiple shops</p>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`step ${index === currentStep ? 'active' : index < currentStep ? 'completed' : ''}`}
          >
            <div className="step-icon">{step.icon}</div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>

      {/* Step Content */}
      {renderStepContent()}
    </div>
  );
};

export default OrderPlacement;

